import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Typography, Paper, Box, Divider, CircularProgress, Button, Snackbar, useMediaQuery, useTheme } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useFontSize } from "./theme/FontSizeContext";
import { useTextColor } from "./theme/ColorContext";
import { useTheme as useAppTheme } from "./theme/ThemeContext";
import { useReaderTypography } from "./theme/ReaderTypographyContext";
import VerseSelectionDock from "./components/VerseSelectionDock";
import SpeechBar from "./components/SpeechBar";
import { useEdition } from "./bible/edition";
import { scrollBehavior } from "./bible/speech";
import { consumeContinueListen, markContinueListen } from "./bible/speechPrefs";
import useSpeech from "./bible/useSpeech";
import { useZenMode } from "./theme/ZenModeContext";
import useFullscreen from "./hooks/useFullscreen";
import useWakeLock from "./hooks/useWakeLock";
import { loadChapterHighlights, setRangeHighlights } from "./bible/highlightStorage";
import { useChapterProgress, ChapterTopProgressBar, ChapterProgressCapsule } from "./components/ChapterProgress";
import { saveLastReadChapter } from "./bible/readingHistoryStorage";
import { setReaderVerseFocus } from "./bible/readerVerseFocus";
import { useReaderPersistGate } from "./bible/readerPersistGate";
import { parseVerseParam } from "./bible/shareUrl";
import { getBookNameByChapterId, parseChapterId } from "./bible/bookNames";
import NoteEditorDialog from "./components/Notes/NoteEditorDialog";
import {
  getChapterNotes,
  getChapterSharedNotes,
  deleteNote,
  requestNotePublicReview,
  withdrawNotePublicReview,
} from "./firebase/notesService";
import { useAuth } from "./firebase/authContext";
import { cleanVerseQuoteText } from "./bible/verseQuote";
import { getReaderContainerSx, getReaderPaperWithRailsSx } from "./theme/readerLayout";
import ReaderHeader from "./components/Reader/ReaderHeader";
import VerseLine, { parseChapterLines } from "./components/Reader/VerseLine";
import ChapterEndCard from "./components/Reader/ChapterEndCard";
import ZenFloatingBar from "./components/Reader/ZenFloatingBar";
import CatalogDrawer from "./components/Reader/CatalogDrawer";
import ReaderLeftRail from "./components/Reader/ReaderLeftRail";
import ReaderRightRail from "./components/Reader/ReaderRightRail";
import QuickSettingsPopover from "./components/Reader/QuickSettingsPopover";
import BookmarkDrawer from "./components/Reader/BookmarkDrawer";
import VerseCommentsDrawer, {
  buildVerseNotesMap,
  expandVerseRangeNumbers,
} from "./components/Reader/VerseCommentsDrawer";
import MobileReaderBottomBar from "./components/Reader/MobileReaderBottomBar";
import ShortcutsHelpDialog from "./components/Reader/ShortcutsHelpDialog";
import SettingsPanel from "./components/SettingsPanel";
import useChapterNavigation from "./components/Reader/useChapterNavigation";
import useChapterSelection from "./components/Reader/useChapterSelection";
import useReaderShortcuts from "./components/Reader/useReaderShortcuts";
import useReadingPosition from "./components/Reader/useReadingPosition";
import usePullNextChapter from "./components/Reader/usePullNextChapter";
import PullNextChapterIndicator from "./components/Reader/PullNextChapterIndicator";

const ChapterDetail = () => {
  const { chapterId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const edition = useEdition();
  const focusVerse = searchParams.get("v");
  const { fontSize } = useFontSize();
  const { textColor } = useTextColor();
  const { fontFamily, lineHeight } = useReaderTypography();
  const { isZenMode, toggleZenMode } = useZenMode();
  const { isFullscreen, isSupported: isFullscreenSupported, toggleFullscreen } =
    useFullscreen();
  const wakeLock = useWakeLock();
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const chapterHoldRef = useRef(null);
  const [allChapters, setAllChapters] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [chapterTitles, setChapterTitles] = useState({});
  const [chapterSectionHeadings, setChapterSectionHeadings] = useState({});
  const [highlights, setHighlights] = useState(() => loadChapterHighlights(chapterId));

  const { currentUser } = useAuth();
  const canPersistReader = useReaderPersistGate(chapterId, currentUser?.uid);
  const [chapterNotes, setChapterNotes] = useState([]);
  const [chapterSharedNotes, setChapterSharedNotes] = useState([]);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [quickSettingsAnchor, setQuickSettingsAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const [verseCommentsEnabled, setVerseCommentsEnabled] = useState(true);
  const [activeCommentVerse, setActiveCommentVerse] = useState(null);

  const versesRef = useRef(null);
  const touchStateRef = useRef({ startX: 0, startY: 0, startTime: 0 });
  const theme = useTheme();
  const { contentWidth = 80 } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const ink = isDark ? "#e2c48a" : "#8b6914";
  const wash = isDark ? "rgba(226, 196, 138, 0.16)" : "rgba(196, 162, 101, 0.24)";
  const listenInk = isDark ? "#7ec8c8" : "#2a6f6f";
  const listenWash = isDark ? "rgba(46, 140, 140, 0.18)" : "rgba(42, 111, 111, 0.12)";
  const playedThroughEndRef = useRef(false);
  const handleListenRef = useRef(() => {});
  const listenLang = edition.id === "en" ? "en-US" : "zh-CN";

  const {
    hasPrev,
    hasNext,
    prevChapter,
    nextChapter,
    goPrev,
    goNext,
    goToChapter,
  } = useChapterNavigation({
    allChapters,
    currentIndex,
    navigate,
    edition,
  });

  const fetchNotesForChapter = useCallback(async () => {
    if (!chapterId) {
      setChapterNotes([]);
      setChapterSharedNotes([]);
      return;
    }
    if (typeof getChapterSharedNotes === "function") {
      getChapterSharedNotes(chapterId)
        .then((shared) => setChapterSharedNotes(Array.isArray(shared) ? shared : []))
        .catch(() => setChapterSharedNotes([]));
    }
    if (!currentUser?.uid) {
      setChapterNotes([]);
      return;
    }
    const list = await getChapterNotes(chapterId, currentUser.uid);
    setChapterNotes(list.filter((n) => !n.userId || n.userId === currentUser.uid));
  }, [chapterId, currentUser?.uid]);

  const verseNotesMap = useMemo(
    () => buildVerseNotesMap(chapterNotes, currentUser?.uid),
    [chapterNotes, currentUser?.uid]
  );

  const publicVerseNotesMap = useMemo(() => {
    const map = {};
    (chapterSharedNotes || []).forEach((note) => {
      if (!note || (currentUser?.uid && (note.authorId === currentUser.uid || note.userId === currentUser.uid))) {
        return;
      }
      const verses = expandVerseRangeNumbers(note.verseRange);
      verses.forEach((v) => {
        if (!map[v]) map[v] = [];
        map[v].push(note);
      });
    });
    return map;
  }, [chapterSharedNotes, currentUser?.uid]);

  useEffect(() => {
    fetchNotesForChapter();
    const handleNotesChanged = () => fetchNotesForChapter();
    window.addEventListener("bible_notes_changed", handleNotesChanged);
    window.addEventListener("bible_shared_notes_changed", handleNotesChanged);
    return () => {
      window.removeEventListener("bible_notes_changed", handleNotesChanged);
      window.removeEventListener("bible_shared_notes_changed", handleNotesChanged);
    };
  }, [fetchNotesForChapter]);

  useEffect(() => {
    setHighlights(loadChapterHighlights(chapterId));
  }, [chapterId]);

  const speech = useSpeech({
    onComplete: () => {
      if (!speech.autoAdvance || !playedThroughEndRef.current) return;
      if (!nextChapter) return;
      markContinueListen(nextChapter.id);
      goToChapter(nextChapter.id);
    },
  });

  useEffect(() => {
    if (!chapterId || !canPersistReader) return;
    const parsed = parseChapterId(chapterId);
    saveLastReadChapter(
      {
        chapterId,
        bookName: getBookNameByChapterId(chapterId, edition.id === "en"),
        chapterNumber: parsed.chapterNumber || 1,
        editionId: edition.id,
      },
      currentUser?.uid
    );
  }, [chapterId, edition.id, canPersistReader, currentUser?.uid]);

  useEffect(() => {
    let cancelled = false;
    if (!chapterHoldRef.current) setLoading(true);
    Promise.all([edition.loadChapters(), edition.loadTitles()]).then(
      ([chapters, titlesModule]) => {
        if (cancelled) return;
        const index = chapters.findIndex((ch) => ch.id === chapterId);
        setAllChapters(chapters);
        setCurrentIndex(index);
        const targetChapter = chapters[index];
        chapterHoldRef.current = targetChapter || null;
        setChapter(targetChapter);
        if (targetChapter && canPersistReader) {
          saveLastReadChapter(
            {
              chapterId: targetChapter.id,
              bookName: getBookNameByChapterId(targetChapter.id, edition.id === "en"),
              chapterNumber: targetChapter.chapterNumber,
              title: targetChapter.title,
              editionId: edition.id,
            },
            currentUser?.uid
          );
        }
        setChapterTitles(titlesModule.chapterTitles || {});
        setChapterSectionHeadings(titlesModule.chapterSectionHeadings || {});
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [chapterId, edition, canPersistReader, currentUser?.uid]);

  useEffect(() => {
    speech.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stop is stable
  }, [chapterId, edition.id]);

  const speakingVerse = speech.current?.verse;
  useEffect(() => {
    if (!speakingVerse) return;
    versesRef.current
      ?.querySelector(`[data-verse="${speakingVerse}"]`)
      ?.scrollIntoView({ block: "center", behavior: scrollBehavior() });
  }, [speakingVerse]);

  const lines = useMemo(() => {
    if (!chapter) return [];
    return chapter.content.split("\r\n").filter((line) => line.trim());
  }, [chapter]);

  const parsedLines = useMemo(() => parseChapterLines(lines), [lines]);

  const verseTextMap = useMemo(() => {
    if (!parsedLines.length) return {};
    return parsedLines.reduce((acc, item) => {
      if (item.number) {
        acc[item.number] = item.text;
      }
      return acc;
    }, {});
  }, [parsedLines]);

  const verseNumbers = useMemo(
    () => Object.keys(verseTextMap).map(Number).filter(Number.isFinite).sort((a, b) => a - b),
    [verseTextMap]
  );

  const neighborVerse = useCallback(
    (verse, direction) => {
      const index = verseNumbers.indexOf(verse);
      return index === -1 ? null : (verseNumbers[index + direction] ?? null);
    },
    [verseNumbers]
  );

  const bookName = useMemo(() => {
    const rawBook = chapter?.bookName || "";
    return rawBook.includes("-") ? rawBook.split("-").slice(1).join("-") : rawBook;
  }, [chapter]);

  const {
    selectedRange,
    selectedCount,
    previewByFormat,
    buildChapterReference,
    buildFormatText,
    isVerseSelected,
    handleVerseClick,
    expandStart,
    expandEnd,
    clearSelection,
    handleCopySelection,
    handleShareSelection,
    handleCopyTextOnly,
    handleCopyEnhanced,
    handleShareWhatsApp,
  } = useChapterSelection({
    chapterId,
    chapter,
    bookName,
    loading,
    focusVerse,
    edition,
    verseTextMap,
    neighborVerse,
    versesRef,
    isMobile,
    noteEditorOpen,
  });

  const handleHighlight = useCallback(
    (colorId) => {
      if (!selectedRange.start) return;
      const start = selectedRange.start;
      const end = selectedRange.end || selectedRange.start;
      const updated = setRangeHighlights(chapterId, start, end, colorId);
      setHighlights(updated);
    },
    [chapterId, selectedRange.end, selectedRange.start]
  );

  const playSpeech = speech.play;
  const handleListen = useCallback(
    ({ forceChapter = false, fromVerse } = {}) => {
      const lastVerse = verseNumbers[verseNumbers.length - 1];
      const start = fromVerse
        ? fromVerse
        : !forceChapter && selectedRange.start
          ? selectedRange.start
          : verseNumbers[0];
      const end = fromVerse
        ? lastVerse
        : !forceChapter && selectedRange.end
          ? selectedRange.end
          : lastVerse;
      if (!start || !end) return;
      const items = [];
      for (let verse = start; verse <= end; verse += 1) {
        const text = verseTextMap[String(verse)];
        if (text) {
          items.push({ verse, text, lang: listenLang });
        }
      }
      playedThroughEndRef.current = end === lastVerse;
      playSpeech(items, {
        title: chapter?.title?.replace("# ", "") || "",
        artist: edition.versionName,
      });
    },
    [
      chapter,
      edition.versionName,
      listenLang,
      playSpeech,
      selectedRange.end,
      selectedRange.start,
      verseNumbers,
      verseTextMap,
    ]
  );
  handleListenRef.current = handleListen;

  useEffect(() => {
    if (loading || !chapter || chapter.id !== chapterId) return undefined;
    if (!consumeContinueListen(chapterId)) return undefined;
    const timer = window.setTimeout(() => {
      handleListenRef.current({ forceChapter: true });
    }, 220);
    return () => window.clearTimeout(timer);
  }, [chapter, chapterId, loading]);

  const sectionList = useMemo(() => {
    if (!chapter) return [];
    return (chapterSectionHeadings[chapter.id] || [])
      .map((heading) => ({
        verse: Number(heading.verse),
        title: heading.title,
      }))
      .filter((heading) => Number.isFinite(heading.verse));
  }, [chapter, chapterSectionHeadings]);

  const sectionHeadings = useMemo(() => {
    return sectionList.reduce((acc, heading) => {
      if (!acc[heading.verse]) {
        acc[heading.verse] = [];
      }
      acc[heading.verse].push(heading.title);
      return acc;
    }, {});
  }, [sectionList]);

  const bookTotalChapters = useMemo(() => {
    if (!chapter || !allChapters.length) return 0;
    const rawBook = chapter.bookName || "";
    return allChapters.filter((c) => (c.bookName || "") === rawBook).length;
  }, [chapter, allChapters]);

  const chapterProgress = useChapterProgress({
    chapterId,
    verseNumbers,
    verseTextMap,
    sectionList,
    lang: edition.id,
  });

  const {
    resumeVerse,
    handleJumpToResumeVerse,
    dismissResumeVerse,
    bookmarks,
    chapterBookmarks,
    isCurrentVerseBookmarked,
    handleToggleCurrentBookmark,
    handleRemoveBookmark,
  } = useReadingPosition({
    chapterId,
    bookName,
    chapterNumber: chapter?.chapterNumber || 1,
    currentVerse: chapterProgress.currentVerse,
    isScrolling: chapterProgress.isScrolling,
    focusVerse,
    verseTextMap,
    scrollToVerse: chapterProgress.scrollToVerse,
    uid: currentUser?.uid,
    persist: canPersistReader,
  });

  useEffect(() => {
    if (!chapterId || !canPersistReader) return;
    const routeVerse = parseVerseParam(focusVerse)?.start || null;
    setReaderVerseFocus(
      {
        chapterId,
        routeVerse,
        selectedVerse: selectedRange.start || routeVerse || undefined,
        currentVerse: chapterProgress.currentVerse || undefined,
      },
      currentUser?.uid
    );
  }, [
    chapterId,
    selectedRange.start,
    chapterProgress.currentVerse,
    focusVerse,
    canPersistReader,
    currentUser?.uid,
  ]);

  const currentChapterText = useMemo(() => {
    if (!chapter) return "";
    if (edition.ui?.currentChapterLabel) {
      return edition.ui.currentChapterLabel(
        bookName,
        chapter.chapterNumber,
        bookTotalChapters
      );
    }
    return `当前是：${bookName} 第 ${chapter.chapterNumber} 章${
      bookTotalChapters ? `（共 ${bookTotalChapters} 章）` : ""
    }`;
  }, [bookName, bookTotalChapters, chapter, edition.ui]);

  const speechChapterTitle = useMemo(() => {
    if (!chapter) return "";
    if (edition.id === "en") {
      const isPsalm = (bookName || "").toLowerCase().includes("psalm");
      return isPsalm
        ? `Psalm ${chapter.chapterNumber}`
        : `${bookName || chapter.title.replace("# ", "")} ${chapter.chapterNumber || ""}`.trim();
    }
    const unit = bookName === "诗篇" ? "篇" : "章";
    if (bookName && chapter.chapterNumber) {
      return bookTotalChapters
        ? `${bookName} 第${chapter.chapterNumber}/${bookTotalChapters}${unit}`
        : `${bookName} 第${chapter.chapterNumber}${unit}`;
    }
    return chapter.title.replace("# ", "").replace(/\s*-\s*/, " ");
  }, [bookName, bookTotalChapters, chapter, edition.id]);

  const speechChapterSubtitle = useMemo(() => {
    if (!chapter) return "";
    return chapterTitles[chapter.id] || "";
  }, [chapter, chapterTitles]);

  useEffect(() => {
    if (!chapter) return;
    const plainTitle = chapter.title.replace("# ", "");
    document.title = `${plainTitle} | ${edition.appTitle}`;
    return () => {
      document.title = edition.appTitle;
    };
  }, [chapter, edition.appTitle]);

  const handleTakeNoteFromDock = () => {
    if (!selectedRange.start) return;
    const start = selectedRange.start;
    const end = selectedRange.end || selectedRange.start;
    const rangeStr = start === end ? String(start) : `${start}-${end}`;
    const text = buildFormatText("plain", start, end);

    setActiveNote({
      chapterId,
      bookName,
      chapterNumber: chapter?.chapterNumber || 1,
      verseRange: rangeStr,
      verseText: text,
    });
    setNoteEditorOpen(true);
  };

  const handleOpenVerseNote = (verseNum, verseTxt, existingNote = null) => {
    if (existingNote) {
      setActiveNote(existingNote);
    } else {
      setActiveNote({
        chapterId,
        bookName,
        chapterNumber: chapter?.chapterNumber || 1,
        verseRange: String(verseNum),
        verseText: cleanVerseQuoteText(verseTxt),
      });
    }
    setNoteEditorOpen(true);
  };

  useReaderShortcuts({
    hasSelection: Boolean(selectedRange.start),
    hasModalOpen:
      catalogOpen ||
      bookmarksOpen ||
      Boolean(quickSettingsAnchor) ||
      settingsOpen ||
      noteEditorOpen ||
      shortcutsHelpOpen,
    onPrevChapter: hasPrev ? goPrev : undefined,
    onNextChapter: hasNext ? goNext : undefined,
    onToggleCatalog: () => setCatalogOpen((prev) => !prev),
    onToggleSettings: () =>
      setQuickSettingsAnchor((prev) => (prev ? null : document.body)),
    onToggleBookmarks: () => setBookmarksOpen((prev) => !prev),
    onToggleHelp: () => setShortcutsHelpOpen((prev) => !prev),
  });

  const pullNextState = usePullNextChapter({
    hasNext,
    nextChapter,
    onNext: goNext,
    isBlocked:
      Boolean(selectedRange.start) ||
      speech.status !== "idle" ||
      catalogOpen ||
      bookmarksOpen ||
      Boolean(activeCommentVerse) ||
      Boolean(quickSettingsAnchor) ||
      settingsOpen ||
      noteEditorOpen ||
      shortcutsHelpOpen,
  });

  if (loading && !chapter) {
    return <CircularProgress />;
  }

  if (!chapter) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" color="error">
            {edition.ui.notFound}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const hasSelection = Boolean(selectedRange.start);

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStateRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      startTime: Date.now(),
    };
  };

  const handleTouchEnd = (e) => {
    if (e.changedTouches.length !== 1) return;
    const touch = e.changedTouches[0];
    const diffX = touch.clientX - touchStateRef.current.startX;
    const diffY = touch.clientY - touchStateRef.current.startY;
    const elapsed = Date.now() - touchStateRef.current.startTime;

    if (Math.abs(diffX) > 70 && Math.abs(diffY) < 60 && elapsed < 500) {
      if (diffX < 0 && hasNext) {
        goNext();
      } else if (diffX > 0 && hasPrev) {
        goPrev();
      }
    }
  };

  return (
    <>
      <ChapterTopProgressBar
        scrollPercent={chapterProgress.scrollPercent}
        visible={chapterProgress.progressStyle !== "hidden"}
      />
      <ReaderLeftRail
        contentWidth={contentWidth}
        isZenMode={isZenMode}
        edition={edition}
        onOpenCatalog={() => setCatalogOpen(true)}
        onOpenSettings={(anchorEl) => setQuickSettingsAnchor(anchorEl || document.body)}
        onOpenBookmarks={() => setBookmarksOpen(true)}
        bookmarksCount={bookmarks.length}
        hasChapterBookmark={chapterBookmarks.length > 0}
        onOpenNotes={() => navigate("/notes")}
        notesCount={chapterNotes.length}
        onGoHome={() => navigate(edition.homePath || "/")}
        wakeLock={wakeLock}
        isFullscreen={isFullscreen}
        isFullscreenSupported={isFullscreenSupported}
        toggleFullscreen={toggleFullscreen}
        toggleZenMode={toggleZenMode}
      />
      <ReaderRightRail
        contentWidth={contentWidth}
        isZenMode={isZenMode}
        edition={edition}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onPrev={goPrev}
        onNext={goNext}
        verseCommentsEnabled={verseCommentsEnabled}
        onToggleVerseComments={() => setVerseCommentsEnabled((v) => !v)}
        verseCommentsCount={chapterNotes.length}
        onScrollToTop={chapterProgress.scrollToTop}
      />
      <Container
        maxWidth={false}
        sx={getReaderContainerSx(contentWidth, { hasSelection })}
      >
        <Paper
          elevation={0}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={getReaderPaperWithRailsSx(contentWidth)}
        >
          <ReaderHeader
            chapter={chapter}
            bookName={bookName}
            bookTotalChapters={bookTotalChapters}
            chapterSubtitle={chapterTitles[chapter.id]}
            currentChapterText={currentChapterText}
            verseCount={verseNumbers.length}
            verseTextMap={verseTextMap}
            edition={edition}
            isMobile={isMobile}
            onBack={() => navigate(-1)}
            wakeLock={wakeLock}
            isFullscreen={isFullscreen}
            isFullscreenSupported={isFullscreenSupported}
            toggleFullscreen={toggleFullscreen}
            isZenMode={isZenMode}
            toggleZenMode={toggleZenMode}
          />

          <SpeechBar
            supported={speech.supported}
            status={speech.status}
            current={speech.current}
            index={speech.index}
            total={speech.total}
            hasSelection={Boolean(selectedRange.start)}
            labels={edition.ui}
            chapterTitle={speechChapterTitle}
            bookChapter={Number(chapter?.chapterNumber) || 0}
            bookTotalChapters={bookTotalChapters}
            chapterSubtitle={speechChapterSubtitle}
            onChapterClick={chapterProgress.scrollToTop}
            rate={speech.rate}
            onRateChange={speech.setRate}
            autoAdvance={speech.autoAdvance}
            onAutoAdvanceChange={speech.setAutoAdvance}
            sleepTimer={speech.sleepTimer}
            onSleepTimerChange={speech.setSleepTimer}
            voices={speech.voices}
            voiceURI={speech.voiceURI}
            onVoiceChange={speech.setVoiceURI}
            voiceLangs={[listenLang]}
            onPlay={() => handleListen()}
            onPause={speech.pause}
            onResume={speech.resume}
            onStop={speech.stop}
            onSkip={speech.skip}
            sections={sectionList}
            onPlaySection={(verse) => handleListen({ fromVerse: verse })}
          />

          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Button
                variant="text"
                startIcon={<NavigateBeforeIcon />}
                onClick={goPrev}
                disabled={!hasPrev}
              >
                {prevChapter ? prevChapter.title.replace("# ", "") : ""}
              </Button>
              <Button
                variant="text"
                endIcon={<NavigateNextIcon />}
                onClick={goNext}
                disabled={!hasNext}
              >
                {nextChapter ? nextChapter.title.replace("# ", "") : ""}
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box ref={versesRef} sx={{ mb: 4 }}>
            {parsedLines.map((item) => {
              const { index, number, verseNumber, prevVerseNumber, nextVerseNumber } = item;
              const headingList =
                verseNumber && sectionHeadings[verseNumber]
                  ? sectionHeadings[verseNumber]
                  : [];
              const selected = isVerseSelected(number);
              const speaking = Boolean(
                verseNumber && speech.current?.verse === verseNumber
              );
              const isStart = selected && verseNumber === selectedRange.start;
              const isEnd = selected && verseNumber === selectedRange.end;
              const prevSelected = prevVerseNumber ? isVerseSelected(prevVerseNumber) : false;
              const nextSelected = nextVerseNumber ? isVerseSelected(nextVerseNumber) : false;
              const hlColor = verseNumber ? highlights[String(verseNumber)] : null;
              const verseNotes = verseNumber ? verseNotesMap[verseNumber] || [] : [];
              const matchingNote = verseNotes[0] || null;

              return (
                <VerseLine
                  key={index}
                  item={item}
                  headingList={headingList}
                  selected={selected}
                  prevSelected={prevSelected}
                  nextSelected={nextSelected}
                  isStart={isStart}
                  isEnd={isEnd}
                  speaking={speaking}
                  hasSelection={hasSelection}
                  hlColor={hlColor}
                  matchingNote={matchingNote}
                  verseNotes={verseNotes}
                  verseCommentsEnabled={verseCommentsEnabled}
                  isZenMode={isZenMode}
                  isDark={isDark}
                  isMobile={isMobile}
                  fontSize={fontSize}
                  lineHeight={lineHeight}
                  fontFamily={fontFamily}
                  textColor={textColor}
                  ink={ink}
                  wash={wash}
                  listenInk={listenInk}
                  listenWash={listenWash}
                  edition={edition}
                  onVerseClick={handleVerseClick}
                  onListenFromVerse={(v) => handleListen({ fromVerse: v })}
                  onOpenVerseNote={handleOpenVerseNote}
                  onOpenVerseComments={(v, txt) =>
                    setActiveCommentVerse({ verse: v, text: cleanVerseQuoteText(txt) })
                  }
                  onCopyTextOnly={handleCopyTextOnly}
                  onCopyEnhanced={handleCopyEnhanced}
                  onShareWhatsApp={handleShareWhatsApp}
                />
              );
            })}
          </Box>

          <Divider sx={{ my: 3 }} />

          <ChapterEndCard
            hasPrev={hasPrev}
            hasNext={hasNext}
            prevChapter={prevChapter}
            nextChapter={nextChapter}
            nextChapterSubtitle={nextChapter ? chapterTitles[nextChapter.id] || "" : ""}
            onPrev={goPrev}
            onNext={goNext}
            onOpenCatalog={() => setCatalogOpen(true)}
            isCompleted={chapterProgress.isCompleted}
            onToggleCompleted={chapterProgress.toggleCompleted}
            highlightsCount={Object.keys(highlights || {}).length}
            notesCount={chapterNotes.length}
            sharedNotes={chapterSharedNotes}
            edition={edition}
            isMobile={isMobile}
          />

          <PullNextChapterIndicator
            isPulling={pullNextState.isPulling}
            pullProgress={pullNextState.pullProgress}
            isReadyToAdvance={pullNextState.isReadyToAdvance}
            hasNext={hasNext}
            nextChapter={nextChapter}
            nextChapterSubtitle={nextChapter ? chapterTitles[nextChapter.id] || "" : ""}
            edition={edition}
          />
        </Paper>

        <CatalogDrawer
          open={catalogOpen}
          onClose={() => setCatalogOpen(false)}
          allChapters={allChapters}
          currentChapterId={chapter.id}
          chapterTitles={chapterTitles}
          onSelectChapter={goToChapter}
          edition={edition}
          isMobile={isMobile}
        />

        <BookmarkDrawer
          open={bookmarksOpen}
          onClose={() => setBookmarksOpen(false)}
          bookmarks={bookmarks}
          currentChapterId={chapter.id}
          currentVerse={chapterProgress.currentVerse}
          isCurrentVerseBookmarked={isCurrentVerseBookmarked}
          onToggleCurrentBookmark={handleToggleCurrentBookmark}
          onSelectBookmark={(bm) => {
            if (bm.chapterId === chapter.id) {
              chapterProgress.scrollToVerse(bm.verse);
            } else {
              navigate(`${edition.chapterPath(bm.chapterId)}?v=${bm.verse}`);
            }
          }}
          onRemoveBookmark={handleRemoveBookmark}
          edition={edition}
          isMobile={isMobile}
        />

        <VerseCommentsDrawer
          open={Boolean(activeCommentVerse)}
          onClose={() => setActiveCommentVerse(null)}
          bookName={bookName}
          chapterNumber={chapter?.chapterNumber || 1}
          verse={activeCommentVerse?.verse || 1}
          verseText={activeCommentVerse?.text || ""}
          myNotes={
            activeCommentVerse?.verse
              ? verseNotesMap[activeCommentVerse.verse] || []
              : []
          }
          publicNotes={
            activeCommentVerse?.verse
              ? publicVerseNotesMap[activeCommentVerse.verse] || []
              : []
          }
          onAddNote={(v, txt) => {
            handleOpenVerseNote(v, txt, null);
          }}
          onEditNote={(note) => {
            handleOpenVerseNote(
              activeCommentVerse?.verse || 1,
              activeCommentVerse?.text || "",
              note
            );
          }}
          onDeleteNote={async (note) => {
            await deleteNote(note.id, currentUser);
            fetchNotesForChapter();
          }}
          onRequestPublic={async (note) => {
            if (typeof requestNotePublicReview === "function") {
              await requestNotePublicReview(note.id, currentUser);
              fetchNotesForChapter();
            }
          }}
          onWithdrawPublic={async (note) => {
            if (typeof withdrawNotePublicReview === "function") {
              await withdrawNotePublicReview(note.id, currentUser);
              fetchNotesForChapter();
            }
          }}
          edition={edition}
          isMobile={isMobile}
        />

        <Snackbar
          open={Boolean(resumeVerse)}
          autoHideDuration={6500}
          onClose={dismissResumeVerse}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          message={
            edition.id === "en"
              ? `Last read at Verse ${resumeVerse}`
              : `上次读到第 ${resumeVerse} 节`
          }
          action={
            <Button
              color="primary"
              size="small"
              onClick={handleJumpToResumeVerse}
              sx={{ fontWeight: 700 }}
            >
              {edition.id === "en" ? "Jump" : "跳转"}
            </Button>
          }
        />

        <QuickSettingsPopover
          open={Boolean(quickSettingsAnchor)}
          anchorEl={quickSettingsAnchor}
          onClose={() => setQuickSettingsAnchor(null)}
          onOpenMoreSettings={() => setSettingsOpen(true)}
          edition={edition}
          isMobile={isMobile}
        />

        <SettingsPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />

        <ShortcutsHelpDialog
          open={shortcutsHelpOpen}
          onClose={() => setShortcutsHelpOpen(false)}
          edition={edition}
        />

        <VerseSelectionDock
          open={hasSelection && !noteEditorOpen}
          reference={buildChapterReference(selectedRange.start, selectedRange.end)}
          verseCount={selectedCount}
          previewByFormat={previewByFormat}
          canExpandStart={Boolean(
            selectedRange.start && neighborVerse(selectedRange.start, -1)
          )}
          canExpandEnd={Boolean(
            selectedRange.end && neighborVerse(selectedRange.end, 1)
          )}
          onExpandStart={expandStart}
          onExpandEnd={expandEnd}
          onCopy={handleCopySelection}
          onShare={handleShareSelection}
          onPlay={() => {
            if (selectedCount === 1) {
              handleListen({ fromVerse: selectedRange.start });
            } else {
              handleListen();
            }
          }}
          onClear={clearSelection}
          onHighlight={handleHighlight}
          onTakeNote={handleTakeNoteFromDock}
          activeHighlightColor={selectedRange.start ? highlights[String(selectedRange.start)] : null}
          copyUi={edition.copy}
          isMobile={isMobile}
        />

        <ZenFloatingBar
          open={isZenMode}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPrev={goPrev}
          onNext={goNext}
          onExitZen={toggleZenMode}
          wakeLock={wakeLock}
          edition={edition}
        />

        <MobileReaderBottomBar
          isMobile={isMobile}
          isZenMode={isZenMode}
          hasSelection={hasSelection}
          speechStatus={speech.status}
          scrollDirection={chapterProgress.scrollDirection}
          scrollPercent={chapterProgress.scrollPercent}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onOpenCatalog={() => setCatalogOpen(true)}
          onPrev={goPrev}
          onOpenSettings={(anchorEl) =>
            setQuickSettingsAnchor(anchorEl || document.body)
          }
          onNext={goNext}
          edition={edition}
        />
      </Container>

      <ChapterProgressCapsule
        chapterTitle={chapter?.title?.replace("# ", "") || ""}
        currentVerse={chapterProgress.currentVerse}
        totalVerses={chapterProgress.totalVerses}
        firstVerse={chapterProgress.firstVerse}
        lastVerse={chapterProgress.lastVerse}
        scrollPercent={chapterProgress.scrollPercent}
        isScrolling={chapterProgress.isScrolling}
        scrollDirection={chapterProgress.scrollDirection}
        isCompleted={chapterProgress.isCompleted}
        progressStyle={chapterProgress.progressStyle}
        estimatedMinutesRemaining={chapterProgress.estimatedMinutesRemaining}
        currentSection={chapterProgress.currentSection}
        sectionList={sectionList}
        verseTextMap={verseTextMap}
        hasSelection={hasSelection}
        isZenMode={isZenMode}
        toggleCompleted={chapterProgress.toggleCompleted}
        scrollToVerse={chapterProgress.scrollToVerse}
        scrollToTop={chapterProgress.scrollToTop}
        setStyle={chapterProgress.setStyle}
        labels={edition.ui}
      />

      {noteEditorOpen && (
        <NoteEditorDialog
          open={noteEditorOpen}
          onClose={() => {
            setNoteEditorOpen(false);
            setActiveNote(null);
          }}
          initialNote={activeNote?.id ? activeNote : null}
          chapterId={activeNote?.chapterId || chapterId}
          bookName={activeNote?.bookName || bookName}
          chapterNumber={activeNote?.chapterNumber || chapter?.chapterNumber || 1}
          verseRange={activeNote?.verseRange || ""}
          verseText={activeNote?.verseText || ""}
          onSaved={() => {
            fetchNotesForChapter();
          }}
        />
      )}
    </>
  );
};

export default ChapterDetail;
